import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { MeetingRatingStats } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { MonthCalendar } from "@/components/meeting-calendar/MonthCalendar";
import { WeekCalendar } from "@/components/meeting-calendar/WeekCalendar";
import { fetchMeetingRatingStats } from "@/lib/clinician-api";
import { isoDay, parseIsoDay, startOfDay } from "@/lib/meeting-calendar";
import { MeetingsList } from "./MeetingsList";

// TES-1233 (Emelie 2026-10-11, TES-1236 point 1) — "Kalender ska vara
// standardvyn under Möten." The view and the first day live in the URL
// (`?vy=lista`, `?dag=2026-10-14`) so a reload, a bookmark and the browser's
// back button keep the doctor where she was.
type View = "week" | "month" | "list";

const VIEW_PARAM = "vy";
const DAY_PARAM = "dag";

const VIEWS: { key: View; param: string | null; label: string }[] = [
  { key: "week", param: null, label: "7 dagar" },
  { key: "month", param: "manad", label: "Månad" },
  { key: "list", param: "lista", label: "Lista" },
];

export function MeetingsRoute() {
  const { signOut } = useAuth();
  const [params, setParams] = useSearchParams();
  const viewParam = params.get(VIEW_PARAM);
  const view: View =
    viewParam === "lista" ? "list" : viewParam === "manad" ? "month" : "week";
  const anchor = parseIsoDay(params.get(DAY_PARAM)) ?? startOfDay(new Date());

  // TES-434 B — the clinic's aggregate of "Hur var ditt vårdmöte?".
  const [ratingStats, setRatingStats] = useState<MeetingRatingStats | null>(
    null,
  );
  useEffect(() => {
    fetchMeetingRatingStats()
      .then(setRatingStats)
      .catch(() => setRatingStats(null));
  }, []);

  const go = useCallback(
    (next: { view?: View; day?: Date | null }) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next.view !== undefined) {
            const param = VIEWS.find((v) => v.key === next.view)?.param;
            if (param) p.set(VIEW_PARAM, param);
            else p.delete(VIEW_PARAM);
          }
          if (next.day !== undefined) {
            // Today is the default and is not written out.
            if (!next.day || isoDay(next.day) === isoDay(new Date())) {
              p.delete(DAY_PARAM);
            } else {
              p.set(DAY_PARAM, isoDay(next.day));
            }
          }
          return p;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  const onUnauthorized = useCallback(() => void signOut(), [signOut]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex flex-wrap items-end justify-between gap-md">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Möten</h1>
          {ratingStats && ratingStats.count > 0 ? (
            <p className="text-sm text-textSecondary">
              Patientbetyg: {ratingStats.average?.toFixed(1)} av 5 (
              {ratingStats.count} svar) · fördelning{" "}
              {ratingStats.distribution
                .map((n, i) => `${i + 1}★ ${n}`)
                .join(" · ")}
            </p>
          ) : null}
        </div>
        <div
          role="group"
          aria-label="Visa som"
          className="inline-flex rounded-md border border-surfaceMuted bg-surface p-[2px]"
        >
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              aria-pressed={view === v.key}
              onClick={() => go({ view: v.key })}
              className={`rounded-sm px-md py-xs text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                view === v.key
                  ? "bg-brand text-textOnBrand"
                  : "text-textSecondary hover:text-textPrimary"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {view === "list" ? (
        <MeetingsList />
      ) : view === "month" ? (
        <MonthCalendar
          anchor={anchor}
          onAnchor={(day) => go({ day })}
          // "Klick på en dag öppnar den dagen i sjudagarsvyn."
          onOpenDay={(day) => go({ view: "week", day })}
          onUnauthorized={onUnauthorized}
        />
      ) : (
        <WeekCalendar
          anchor={anchor}
          onAnchor={(day) => go({ day })}
          onUnauthorized={onUnauthorized}
        />
      )}
    </div>
  );
}
